import { createSelector } from 'reselect';
import { findFontForStyle, FontStyle } from '@bynder-studio/structured-text';
import permissions from 'packages/configs/permissions';
import { UserState } from './types';

export const getUser = (state): UserState => state.user;
export const getIdentityId = (state) => getUser(state)?.user?.identityId;
export const getUserTimeZone = (state): string => state.user && state.user.user && state.user.user.timeZone;
export const customerIdSelector = (state): UserState['currentCompany'] => state.user.currentCompany;

/** Do not use directly. Use access rights hook */
export const isPlatformAllowedSelector = createSelector(
    (state) => state.user.accessibleFeatures,
    (accessibleFeatures) => {
        const isPlatformAllowed = (features: string[], strict = true): boolean => {
            const fn = strict ? 'every' : 'some';

            return features[fn]((feature) => accessibleFeatures.includes(feature));
        };

        return { isPlatformAllowed, dep: accessibleFeatures };
    },
);

export const isBynderScopeAllowedSelector = createSelector(
    (state) => state.user?.bynderScopes,
    (bynderScopes) => {
        return (scopes: string[]): boolean =>
            Array.isArray(bynderScopes) && scopes.some((scope) => bynderScopes.includes(scope));
    },
);

/** Do not use directly. Use access rights hook */
export const isAccountRoleAssignedSelector = createSelector(
    (state) => state.user?.roles,
    (roles) => {
        return (role: string): boolean => {
            if (roles) {
                return roles.includes(role);
            }

            return false;
        };
    },
);

export const getFonts = (state: { user: UserState }) => getUser(state).fonts;

export const getDefaultFontId = createSelector(
    [getFonts, (state: { user: UserState }) => getUser(state).defaultFontId],
    (fonts, defaultFontId): string | undefined => {
        const firstFamily = fonts?.[0];
        const fallback =
            firstFamily && (findFontForStyle(firstFamily.fonts, FontStyle.REGULAR) ?? firstFamily.fonts[0]);
        const id = defaultFontId ?? fallback?.id;

        return id === undefined ? undefined : String(id);
    },
);

export const getSplitFeatures = (state: { user: UserState }) => state.user.splitFeatures;

export const canWriteFonts = (state: { user: UserState }): boolean =>
    state.user.permissions.includes(permissions.FONTS.WRITE);
